/* eslint-disable no-duplicate-imports */
import type { AxiosError, AxiosRequestConfig } from "axios";
import Axios from "axios";

import { faro } from "@grafana/faro-web-sdk";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { setLegacyMutator } from "@vegainvestments/vega-api-client/client";

import { cleanAxiosInstance } from "./cleanInstance";
import { cleanAxiosInstanceWithToken } from "./cleanInstanceWithToken";
import { swaggerAxiosInstance } from "./customInstance";

const SKIP_INTERCEPTORS_OPTION_KEY = "skipInterceptors";
const SKIP_INTERCEPTORS_WITH_TOKEN_OPTION_KEY = "skipInterceptorsWithToken";

export const sanitizeErrorMessage = (message: string): string =>
  String(message).replace(/\b[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, "[email]");

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export const deriveServiceFromUrl = (url: string): string => {
  if (!url) return "";
  const match = /\/api\/([^/?#]+)\/v\d+\/([^/?#]+)/.exec(url);

  if (!match) return "";

  return `${match[1]}.${match[2]}`;
};

const NUMERIC_SEGMENT_PATTERN = /\/\d+(?=\/|$)/g;

export const normalizeUrlTemplate = (url: string): string => {
  if (!url) return "";
  const path = url.split("?")[0];

  return path
    .replace(UUID_PATTERN, ":uuid")
    .replace(NUMERIC_SEGMENT_PATTERN, "/:id");
};

export const preserveStack = (
  sanitizedError: Error,
  originalError: unknown,
): Error => {
  if (
    originalError instanceof Error &&
    typeof originalError.stack === "string" &&
    originalError.stack !== ""
  ) {
    sanitizedError.stack = originalError.stack;
  }

  return sanitizedError;
};

export const swaggerAxiosClientQuery = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig & {
    [SKIP_INTERCEPTORS_OPTION_KEY]?: boolean;
    [SKIP_INTERCEPTORS_WITH_TOKEN_OPTION_KEY]?: boolean;
  }, // Add the custom option type
): Promise<T> => {
  const source = Axios.CancelToken.source();
  // can be undefined in storybook
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const session = faro?.api?.getSession();

  const shouldSkipInterceptors = isTruthy(
    options && options[SKIP_INTERCEPTORS_OPTION_KEY],
  );

  const shouldSkipInterceptorsWithToken = isTruthy(
    options && options[SKIP_INTERCEPTORS_WITH_TOKEN_OPTION_KEY],
  );

  const axiosInstance = (() => {
    if (shouldSkipInterceptors) {
      return cleanAxiosInstance;
    }

    if (shouldSkipInterceptorsWithToken) {
      return cleanAxiosInstanceWithToken;
    }

    return swaggerAxiosInstance;
  })();

  if ((shouldSkipInterceptors || shouldSkipInterceptorsWithToken) && options) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete options[SKIP_INTERCEPTORS_OPTION_KEY];
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete options[SKIP_INTERCEPTORS_WITH_TOKEN_OPTION_KEY];
  }

  const promise = axiosInstance({
    ...config,
    ...options,
    cancelToken: source.token,
  })
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      // can be undefined in storybook
      const { description: errorMessage } = getErrorMessage(error);
      const url = config.url ?? "";

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      faro?.api?.pushError(
        preserveStack(new Error(sanitizeErrorMessage(errorMessage)), error),
        {
          type: "network",
          context: {
            url,
            urlTemplate: normalizeUrlTemplate(url),
            service: deriveServiceFromUrl(url),
            method: config.method ?? "",
            status: error.response
              ? String(error.response.status)
              : "network-error",
            sessionId: session?.id ?? "unknown",
          },
        },
      );
      console.error("Error during Axios request:", error);
      throw error;
    });

  // @ts-expect-error suggested solution from orval docs
  promise.cancel = () => {
    // Don't report cancellation as an error - it's an intentional action
    console.log("Query was cancelled");
    source.cancel("Query was cancelled");
  };

  return promise;
};

// Bridge local axios interceptors to the @vegainvestments/vega-api-client npm package.
// The npm package's generated endpoints call `apiRequest`, which delegates to this
// mutator so that all requests go through our configured axios instances.
setLegacyMutator(swaggerAxiosClientQuery);

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
